import React, { useState } from 'react';
import { OrganizationRegulation } from './types';
import { TablePagination, useTablePagination } from '../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../shared/useTableSort';

interface Props {
  regulations: OrganizationRegulation[];
  onSelectRegulation: (reg: OrganizationRegulation) => void;
  onCreateRegulation: (data: Partial<OrganizationRegulation>) => void;
}

export const RegulationLibraryTable: React.FC<Props> = ({
  regulations,
  onSelectRegulation,
  onCreateRegulation,
}) => {
  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(regulations);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    reg_code: '',
    framework_id: 'FRM-EASA-AIR-OPS',
    title: '',
    lead_department: 'Flight Operations',
    postholder_owner: '',
    review_frequency_months: 12,
    applicability_summary: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.reg_code || !formData.title || !formData.postholder_owner) return;
    onCreateRegulation(formData);
    setShowModal(false);
    setFormData({
      reg_code: '',
      framework_id: 'FRM-EASA-AIR-OPS',
      title: '',
      lead_department: 'Flight Operations',
      postholder_owner: '',
      review_frequency_months: 12,
      applicability_summary: '',
    });
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', gap: '12px' }}>
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: '0 0 2px 0' }}>Corporate Regulation Library</h3>
          <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
            Aviation regulatory frameworks governing operations, assigned postholder owners, and review schedules
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
          style={{ width: 'auto', padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <span>+ New Regulation Record</span>
        </button>
      </div>

      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Code & Title
              </SortableTh>
              <SortableTh field="lead_department" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Department & Postholder
              </SortableTh>
              <SortableTh field="is_active" sortField={sortField} sortDirection={sortDirection} onSort={requestSort} align="center">
                Status
              </SortableTh>
              <SortableTh field="requirement_count" sortField={sortField} sortDirection={sortDirection} onSort={requestSort} align="center">
                Requirements
              </SortableTh>
              <SortableTh field="covered_count" sortField={sortField} sortDirection={sortDirection} onSort={requestSort} align="center">
                Compliance / Coverage
              </SortableTh>
              <SortableTh field="next_review_date" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Next Review
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((reg) => {
              const coveragePct =
                reg.requirement_count && reg.requirement_count > 0
                  ? Math.round(((reg.covered_count || 0) / reg.requirement_count) * 100)
                  : 0;

              return (
                <tr key={reg.id} style={{ cursor: 'pointer' }} onClick={() => onSelectRegulation(reg)}>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{reg.title}</div>
                    <div style={{ fontSize: '11px', color: '#0284c7', fontFamily: 'monospace' }}>{reg.reg_code}</div>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b' }}>{reg.lead_department}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{reg.postholder_owner}</div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span
                      className={`badge-pill ${
                        reg.status === 'ACTIVE'
                          ? 'badge-success'
                          : reg.status === 'UNDER_REVIEW'
                          ? 'badge-warning'
                          : 'badge-neutral'
                      }`}
                    >
                      {reg.status}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 600, color: '#1e293b' }}>
                    {reg.requirement_count ?? 0}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b' }}>
                      {reg.compliant_count || 0} Compliant ({coveragePct}%)
                    </div>
                  </td>
                  <td style={{ fontSize: '12px', color: '#475569' }}>
                    {reg.next_review_date ? reg.next_review_date.slice(0, 10) : 'Unspecified'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectRegulation(reg);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                    >
                      Detail & Terms →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="regulations"
      />

      {/* Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '540px' }}>
            <div className="modal-header">
              <h3>New Regulation Record</h3>
              <button onClick={() => setShowModal(false)} className="close-btn">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="form-field">
                  <label>Regulation Code</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. REG-LIB-EASA-PART-M"
                    value={formData.reg_code}
                    onChange={(e) => setFormData({ ...formData, reg_code: e.target.value })}
                  />
                </div>

                <div className="form-field">
                  <label>Regulation Title</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. EASA Part-M Continuing Airworthiness Management"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-field">
                    <label>Responsible Department</label>
                    <input
                      type="text"
                      required
                      value={formData.lead_department}
                      onChange={(e) => setFormData({ ...formData, lead_department: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label>Postholder Owner</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. CAMO Manager"
                      value={formData.postholder_owner}
                      onChange={(e) => setFormData({ ...formData, postholder_owner: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>Applicability Summary</label>
                  <textarea
                    rows={3}
                    value={formData.applicability_summary}
                    onChange={(e) => setFormData({ ...formData, applicability_summary: e.target.value })}
                    placeholder="Fleet maintenance and airworthiness requirements..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Save Regulation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
